/**
 * SPEC-08 §6b — central account-label map.
 *
 * The engine emits canonical ENGLISH account names by design (see
 * `backend/src/core/journals.ts`). The UI must map every one of them
 * through a single label dictionary so the consolidated Yevmiye summary
 * never displays a raw English string like `Cash — Restoration Costs`
 * or `FX Gain — Lease` alongside the proper TR rows. This file is the
 * frontend's single source of truth for that map — the existing
 * `mapAccountToTr` in `pages/engagement/tabs/journalEntryComponents.tsx`
 * and the Excel-side `mapAccountToTrExcel` both call into it (or its
 * locale keys) so web + export agree.
 *
 * Identity and amounts are untouched — this is presentation only.
 *
 * The dictionary lives as plain locale i18n keys (`je.account.<slug>`)
 * with a `defaultValue` fallback to the original English string. Missing
 * mappings emit a `console.warn` in dev mode so gaps get caught.
 *
 * Per-class accounts:
 *   Right-of-Use Asset — <Class>       → "Kullanım Hakkı Varlığı — <CL>"
 *   Accumulated Depreciation — <Class> → "Birikmiş Amortisman — <CL>"
 *   Depreciation Expense — <Class>     → "Amortisman Gideri — <CL>"
 *
 * The class suffix passes through `form.assetClass.<ENUM>` so the
 * canonical TR class names already in the locale file are reused.
 */

import type { AssetClass } from "@/types";

/** i18n `t()` shape — accepts dynamic keys + params. */
export type AccountLabelT = (
  key: string,
  params?: { defaultValue?: string; [k: string]: unknown },
) => string;

/** Per-class suffix on accounts the backend may emit. */
const ENGLISH_CLASS_TO_ENUM: Record<string, AssetClass> = {
  Buildings: "BUILDINGS",
  Vehicles: "VEHICLES",
  Machinery: "MACHINERY",
  "Office Equipment": "OFFICE_EQUIPMENT",
  "IT Equipment": "IT_EQUIPMENT",
  Other: "OTHER",
};

/**
 * Canonical English → locale-key map. Every backend account name listed
 * in `core/journals.ts` (and `services/engagement/aggregateJournalEntries
 * .ts`'s per-class rewrite) has an entry here. New backend accounts MUST
 * be added here AND to both `tr.json` / `en.json` `je.account.*` blocks
 * — the UI warns at runtime (dev) on any miss.
 */
const ACCOUNT_KEYS: Record<string, string> = {
  // Class-agnostic ROU + AccDep variants (legacy non-class-split lines).
  "Right-of-Use Asset": "je.account.rouAsset",
  "ROU Asset": "je.account.rouAsset",
  "Accumulated Depreciation — ROU": "je.account.accumulatedDepreciation",
  "Depreciation Expense — ROU": "je.account.depreciationExpense",
  // Liability, ST/LT split.
  "Lease Liability": "je.account.leaseLiability",
  "Lease Liability — Long-Term": "je.account.leaseLiabilityLongTerm",
  "Lease Liability — Short-Term": "je.account.leaseLiabilityShortTerm",
  // Expense / P&L.
  "Interest Expense — Lease": "je.account.interestExpense",
  "Variable Lease Expense": "je.account.variableLeaseExpense",
  // FX.
  "FX Gain — Lease": "je.account.fxGain",
  "FX Loss — Lease": "je.account.fxLoss",
  // Modification / termination / index gains and losses.
  "Gain on Lease Modification": "je.account.gainOnModification",
  "Loss on Lease Modification": "je.account.lossOnModification",
  "Gain on Lease Termination": "je.account.gainOnTermination",
  "Loss on Lease Termination": "je.account.lossOnTermination",
  "Gain on Index Remeasurement": "je.account.gainOnIndexRemeasurement",
  // Cash family.
  "Cash / Bank": "je.account.cashBank",
  Cash: "je.account.cashBank",
  Bank: "je.account.cashBank",
  "Cash — Lease Prepayment at Commencement": "je.account.leasePrepayment",
  "Cash — Restoration Costs": "je.account.cashRestorationCosts",
  "Cash — Termination Penalty": "je.account.cashTerminationPenalty",
  // Provisions / incentives / retained earnings.
  "Prepaid Lease Payments": "je.account.prepaidLeasePayments",
  "Lease Incentive Liability": "je.account.leaseIncentiveLiability",
  "Restoration Provision": "je.account.restorationProvision",
  "Retained Earnings": "je.account.retainedEarnings",
};

/** Track in-process unmapped account warnings so we don't spam the console. */
const warnedMisses = new Set<string>();

function warnUnmapped(accountEn: string): void {
  if (
    typeof console === "undefined" ||
    typeof process === "undefined" ||
    // `process.env.NODE_ENV` is replaced at build time by Vite; only
    // warn in non-production builds so we never leak to end users.
    process.env.NODE_ENV === "production"
  ) {
    return;
  }
  if (warnedMisses.has(accountEn)) return;
  warnedMisses.add(accountEn);
  // eslint-disable-next-line no-console
  console.warn(
    `[ifrs16-tool] Unmapped account label "${accountEn}" — add an entry to ` +
      `src/lib/i18n/accountLabels.ts and the je.account.* locale keys.`,
  );
}

/**
 * Map a backend English account string to its locale label via `t()`.
 * Per-class accounts (`<base> — <ClassName>`) are split on the em-dash
 * and recomposed with the class label.
 */
export function mapAccountLabel(
  accountEn: string,
  t: AccountLabelT,
): string {
  // Per-class accounts: split on ` — `; the suffix is the asset class.
  // Tried longest-base-prefix first so `Accumulated Depreciation — ROU`
  // (the legacy class-agnostic ROU AccDep account) doesn't get treated
  // as a per-class account.
  const PER_CLASS_BASES: Array<[string, string]> = [
    ["Right-of-Use Asset", "je.account.rouAsset"],
    ["Accumulated Depreciation", "je.account.accumulatedDepreciation"],
    ["Depreciation Expense", "je.account.depreciationExpense"],
  ];
  for (const [base, key] of PER_CLASS_BASES) {
    if (accountEn === base) {
      // Bare base — class-agnostic line. Falls through to the fixed map
      // (which has explicit ACCOUNT_KEYS entries for `Right-of-Use Asset`
      // and `Accumulated Depreciation — ROU`).
      break;
    }
    if (accountEn.startsWith(`${base} — `)) {
      const cls = accountEn.slice(base.length + 3); // skip ` — `
      // The legacy class-agnostic ROU AccDep account
      // (`Accumulated Depreciation — ROU`) is in the fixed map below;
      // don't treat it as a per-class split.
      if (cls === "ROU") continue;
      const enumKey = ENGLISH_CLASS_TO_ENUM[cls];
      if (enumKey) {
        return `${t(key)} — ${t(`form.assetClass.${enumKey}`)}`;
      }
      // Unknown class name — fall through to fixed map / dev warn.
      break;
    }
  }

  const localeKey = ACCOUNT_KEYS[accountEn];
  if (localeKey !== undefined) {
    return t(localeKey, { defaultValue: accountEn });
  }
  warnUnmapped(accountEn);
  return accountEn;
}

/** For unit tests / introspection. */
export const ACCOUNT_LABEL_KEYS: Readonly<Record<string, string>> = ACCOUNT_KEYS;
